// product-info-screen.component.ts
import { HttpClient } from '@angular/common/http';
import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router, ActivatedRoute } from '@angular/router';
import { EMPTY, Subscription } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { ProductInterface } from 'src/app/model/products/product-interface';
import { OrganisationService } from 'src/app/services/dataServices/organisation-service/organisationservice';
import { ProductService } from 'src/app/services/dataServices/product-service/productService';
import { ProductVariantService } from 'src/app/services/dataServices/product-variant-service/product-variant.service';
import { DialogService } from 'src/app/services/dialog-service/dialog-service.service';
import { StripeService } from 'src/app/services/stripe-service/stripe.service';
import { UserService } from 'src/app/services/user-service/userservice';
import { MAX_VARIANTS_PER_PRODUCT } from 'src/environments/constants';

@Component({
    selector: 'app-product-info-screen',
    templateUrl: './product-info-screen.component.html',
    styleUrls: [
        './product-info-screen.component.scss',
        '../../../../../app.component.css'
    ],
    standalone: false
})
export class ProductInfoScreenComponent implements OnInit, OnDestroy {
  private activeProductSubscription: Subscription;

  // List of all products for active organisation
  productsOfOrg: ProductInterface[] = [];
  // Filtered to ACTIVE products only
  activeProducts: ProductInterface[] = [];
  // Track expansion per product id
  expandedState = new Map<number, boolean>();
  isLoading: boolean = false;
  listingNameById: Map<number, string> = new Map();
  readonly MAX_VARIANTS_PER_PRODUCT = MAX_VARIANTS_PER_PRODUCT;
  
  constructor(
    public productService: ProductService,
    public productVariantService: ProductVariantService,
    private dialogService: DialogService,
    private stripeService: StripeService,
    public organisationService: OrganisationService,
    public router: Router,
    public route: ActivatedRoute,
    private cd: ChangeDetectorRef,
    private userService: UserService,
    private titleService: Title,
    private cdr: ChangeDetectorRef,
    protected http: HttpClient,
  ) {
    const orgId = this.organisationService.activeItem?.organisationId;
    const isMember = !!this.userService.currentMemberships.find(
      (orga) => orga.organisationId === orgId
    );
    if (!isMember) {
      this.router.navigateByUrl('/marktplatz');
    }
  }

  async ngOnInit() {
    document.documentElement.style.setProperty('--scrollStatus', 'auto');

    const activeOrg = this.organisationService.activeItem;
    if (activeOrg?.organisationId != null) {
      try {
        this.isLoading = true;
        const ok = await this.productService.loadProductsForOrganisation(activeOrg);
        if (ok) {
          let orgURL = '/proxy/api/v0/productservice/products/organisation/' + activeOrg.organisationId
          this.productsOfOrg = await new Promise((resolve, reject) => {
            this.http.get<ProductInterface[]>(orgURL).subscribe(
              //success
              (productDTO) => {
                this.activeProducts = productDTO;
                this.expandedState.clear();
                this.activeProducts.forEach((p) => this.expandedState.set(p.id, false));
                this.titleService.setTitle(`${activeOrg.organisationName} – Produkte - BIMSWARM`);
                this.isLoading = false;
                this.cd.detectChanges();
              },
              //failure
              () => {
                this.isLoading = false;
                this.cd.detectChanges();
                reject(false);
              }
            );
          });
        }
      } catch {
        this.isLoading = false;
        this.cd.detectChanges();
        this.productsOfOrg = [];
        this.activeProducts = [];
      }
    }

    const productIdFromUrl = this.route.snapshot.paramMap.get('productId');
    if (productIdFromUrl) {
      const id = Number(productIdFromUrl);
      this.productService.setActiveItem(id);
      this.productVariantService.ITEM_MAP[':productId'] = productIdFromUrl;
      this.expandedState.set(id, true);

      // In case variantId is also in URL, set active variant
      const variantIdFromUrl = this.route.snapshot.paramMap.get('variantId');
      if (variantIdFromUrl) {
        this.productVariantService.setActiveVariant(Number(variantIdFromUrl));
      }
    }
  }

  listingPackageName(productId: number): string {
    const p = this.productsOfOrg.find(prod => prod.id === productId);
    return p?.listingPkgName ?? '';
  }

  ngOnDestroy() {
    document.documentElement.style.setProperty('--scrollStatus', 'hidden');
    if (this.activeProductSubscription) {
      this.activeProductSubscription.unsubscribe();
    }
  }

  toggle(productId: number): void {
    const current = this.expandedState.get(productId) ?? false;
    this.expandedState.forEach((_, key) => this.expandedState.set(key, false));
    this.expandedState.set(productId, !current);
  }

  onProductCardClick(product: ProductInterface): void {
    this.productService.setActiveItem(product.id);
    this.productVariantService.ITEM_MAP[':productId'] = String(product.id);

    this.router.navigate([
      'organisations',
      this.organisationService.activeItem.organisationId,
      'products',
      product.id
    ]);
  }

  isExpanded(productId: number): boolean { 
    return this.expandedState.get(productId) ?? false;
  }

  latestVersionName(product: ProductInterface): string {
    const variants = product?.productVariants || [];
    if (variants.length === 0) return '—';
    const lastVariant = variants[variants.length - 1];
    const versions = lastVariant?.variantVersions || [];
    if (versions.length === 0) return '—';
    return versions[versions.length - 1]?.name || '—';
  }

  isUserOrganisationAdminOrPlattformAdmin(): boolean {
    return this.userService.isUserAdminOfOrganisationOrPlattformAdmin(this.organisationService.activeOrganisation.organisationId) ?? false;
  }

  onCreateProduct(): void {
    const orgId = this.organisationService.activeItem?.organisationId;
    if (!orgId) return;
    this.router.navigateByUrl(`/organisations/${orgId}/products/create`);
  }

  onVariantAdd(productId: string): void {
    this.router.navigate([
      '/organisations/' +
      this.organisationService.activeItem.organisationId +
      '/products/' +
      productId +
      '/variants/create'
    ]);
  }
  onProductEdit(productId: string): void {
    this.router.navigate([
      '/organisations/' +
      this.organisationService.activeItem.organisationId +
      '/products/' +
      productId +
      '/edit'
    ]);
  }

  licenseProduct(productId: string): void {
    this.router.navigate([
      '/organisations/' +
      this.organisationService.activeOrganisation.organisationId +
      '/products/' +
      productId +
      '/lizenz'
    ]);
  }

  changeProductLicense(productId: string): void {
    this.dialogService.openchangeProductLicenseDialog(
      'Wählen Sie den Tarif, mit dem Sie dieses Produkt veröffentlichen wollen',
      'Abbrechen',
      'Veröffentlichen',
      productId).subscribe(
        (result) => {
          if (result) {
            if (result === 'backdropClick') {
              console.log("Klappt: ", result);
            }
            this.loadActiveProducts();
          }
        },
        (e) => { }
      );
  }

  deleteProduct(productId: string): void {
    this.dialogService
      .openConfirmDialog(
        'Wollen Sie das Produkt wirklich löschen?',
        'Abbrechen',
        'Löschen'
      )
      .subscribe(
        (result) => {
          if (result) {
            this.isLoading = true;
            this.deleteProductNormally(productId);
          }
        },
        (e) => { }
      );
  }

  private async deleteProductNormally(productId: string): Promise<void> {
    this.productService
      .deleteItem(await this.productService.getItem(Number.parseInt(productId)))
      .then(() => {
        this.isLoading = false;
        this.cdr.detectChanges();
        this.navigateAfterDeletion();
      })
      .catch((error) => {
        this.isLoading = false;
        this.cdr.detectChanges();
        console.error('Error deleting product:', error);
        this.dialogService.openDialog(
          'Fehler beim Löschen des Produkts',
          'Das Produkt konnte nicht gelöscht werden.'
        );
      });
  }

  private navigateAfterDeletion(): void {
    this.router.navigate([
      'organisations/' +
      this.organisationService.activeOrganisation.organisationId +
      '/products'
    ]);

    this.dialogService.openDialog(
      'Das Produkt wurde erfolgreich gelöscht',
      ''
    );
  }

  // TO DO: Temporary solution, ProductOverview has to be adjasted to use product service 
  private loadActiveProducts(): void {
    const activeOrg = this.organisationService.activeItem;
    if (activeOrg?.organisationId == null) return;

    this.isLoading = true;
    const orgURL = '/proxy/api/v0/productservice/products/organisation/' + activeOrg.organisationId;

    this.http.get<ProductInterface[]>(orgURL).subscribe({
      next: (productDTO) => {
        this.activeProducts = productDTO;
        this.isLoading = false;
        this.cd.detectChanges();
      },
      error: () => {
        this.isLoading = false;
        this.cd.detectChanges();
      }
    });
  }

  hasReachedVariantLimit(product: ProductInterface): boolean {
    return (product?.productVariants?.length ?? 0) >= MAX_VARIANTS_PER_PRODUCT;
  }
}
